import React, { useState, useCallback } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';

import { AnnouncementBanner } from '@helpcenter/announcement-banner';
import { PORTAL_HOME, REQUEST_CREATE } from '@helpcenter/connect-fragment';
import { ScreenName } from '@helpcenter/constants';
import { DocumentTitle } from '@helpcenter/document-title';
import ErrorBoundary from '@helpcenter/error-boundary/src/error-boundary';
import { withErrorPage } from '@helpcenter/error-page';
import { isUseHelpCenterRefresh } from '@helpcenter/feature-flags';
import { FORGE_PORTAL_HOME, FORGE_REQUEST_CREATE } from '@helpcenter/forge-ui-utils/src/constants';
import { getCloudId } from '@helpcenter/meta';
import { injectIntl } from 'react-intl-next';
import type { IntlShape, WrappedComponentProps } from 'react-intl-next';
import { LazySuspense } from 'react-loosely-lazy';
import { di } from 'react-magnetic-di';
import type { Category } from 'rest/category';
import type { PortalAnnouncement } from 'rest/portal';
import type { openChatSideBar, closeChatSideBar } from 'state/actions/chat-details';
import { BlockedAppsBanner } from 'view/blocked-apps-banner';
import { CategoryList } from 'view/common/category-list';
import { HeaderConnectFragments, FooterConnectFragments } from 'view/common/connect-fragments-on-all-pages';
import { FormPicker } from 'view/common/form-picker';
import { LoadingContainer } from 'view/common/loading-container';
import { useCustomiseSidebarTriggerRef } from 'view/common/pushing-customize-sidebar';
import { SkeletonLoadingContainer } from 'view/common/skeleton-loading-container';
import { ExternalLinks } from 'view/external-links';
import { ForgeFooterModule } from 'view/forge-ui/portal-footer';
import { ForgeHeaderModule } from 'view/forge-ui/portal-header';
import { PaperContent } from 'view/layout/paper';
import { LazyVirtualServiceAgentChatSidebar } from 'view/layout/virtual-service-agent-chat-sidebar';
import { RequestCreateFormHelpText } from 'view/portal-home/request-create-form-help-text';
import { LazyPortalSettingsAnnouncementSidebar } from 'view/portal-settings-announcement-sidebar';
import { LazyPortalSettingsSidebar } from 'view/portal-settings-sidebar';
import { RequestCreateForm } from 'view/request-create-form';
import { RequestCreateFormWithProforma } from 'view/request-create-form/request-create-form-with-proforma';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { Box, xcss, media } from '@atlaskit/primitives';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line no-restricted-imports
import { JiraProjectAri } from '@atlassian/ari';
import {
    isCSMHelpCenter,
    isKoshEnabledForDefaultHelpCenter,
} from '@atlassian/help-center-common-util/advanced-help-center';
import { getEnv } from '@atlassian/help-center-common-util/env';
import Redirect from '@atlassian/help-center-common-util/redirect';
import { SidebarToggle } from '@atlassian/help-center-common-util/sidebar-toggle';
import { useVirtualAgentAvailability } from '@atlassian/help-center-virtual-agent/src/services/virtual-agent-availability';
import { VirtualAgentBanner } from '@atlassian/help-center-virtual-agent-banner';
import { UiModificationsEntryPoint } from '@atlassian/jsm-ui-modifications-view-request-create/src/ui/entry-point';

import {
    LearnByDoing,
    REFERRER_QUERY_PARAM,
    shouldShowLearnByDoingBanner,
} from '@atlassian/learn-by-doing-create-request';
import { RovoServiceAgentWidgetInjector } from '@atlassian/rovo-service-agent-chat-widget';
import { useQueryParams } from '../../search/common';
import { HeaderSinglePortalMode } from '../header-single-portal-mode';
import i18n from './messages';
export interface Props {
    hasProformaForm?: boolean;
    requestTypeName: string | undefined;
    requestTypeBelongsToGroup: boolean;
    portalName: string | undefined;
    portalId?: number;
    projectId?: number;
    loading: boolean;
    requestTypeId: number | undefined;
    requestGroupId: number | undefined;
    sideBarOpen: boolean;
    announcement: PortalAnnouncement | undefined;
    categories: Category[] | undefined;
    canEditAnnouncement?: boolean;
    isUsingLanguageSupport?: boolean;
    isProjectSimplified: boolean;
    isBrandingLoaded?: boolean;
    isLoggedIn: boolean;
    canCustomiseHelpCenter: boolean;
    isProjectAdmin: boolean;
    isAtlassianManagedAccount: boolean;
    defaultLanguageDisplayName?: string;
    contactLink?: string;
    toggleSidebar: (value?: boolean) => void;
    screenName?: ScreenName;
    isChatSideBarOpen?: boolean;
    openChatSideBar?: typeof openChatSideBar;
    closeChatSideBar?: typeof closeChatSideBar;
}

const validateRequestGroupIdPresent = (requestGroupId?: number) => {
    if (requestGroupId === -1) {
        return false;
    }
    return !!requestGroupId;
};

const getConnectPageType = (requestTypeId: number | undefined) => {
    if (requestTypeId) return REQUEST_CREATE;
    return PORTAL_HOME;
};

const getForgePageType = (requestTypeId: number | undefined) => {
    if (requestTypeId) return FORGE_REQUEST_CREATE;
    return FORGE_PORTAL_HOME;
};

const getPortalHomeTitle = ({
    intl,
    portalName,
    requestTypeName,
    requestGroupId,
}: {
    intl: IntlShape;
    portalName: string;
    requestTypeName?: string;
    requestGroupId?: number;
}) => {
    if (requestTypeName) {
        return intl.formatMessage(i18n.requestTypePageTitle, {
            portalName,
            requestTypeName,
        });
    }

    if (requestGroupId) {
        return intl.formatMessage(i18n.portalGroupPageTitle, {
            portalName,
        });
    }

    return intl.formatMessage(i18n.portalPageTitle, {
        portalName,
    });
};

const PortalHome = ({
    intl,
    requestTypeName,
    requestTypeBelongsToGroup,
    portalName,
    portalId,
    projectId,
    loading,
    requestTypeId,
    requestGroupId,
    sideBarOpen,
    announcement,
    categories,
    canEditAnnouncement,
    isProjectSimplified,
    isBrandingLoaded,
    isLoggedIn,
    canCustomiseHelpCenter,
    isProjectAdmin,
    isAtlassianManagedAccount,
    toggleSidebar,
    screenName,
    isChatSideBarOpen,
    hasProformaForm,
    openChatSideBar: openChatSideBarAction,
    closeChatSideBar: closeChatSideBarAction,
}: Props & WrappedComponentProps) => {
    di(useVirtualAgentAvailability, ExternalLinks);
    di(isCSMHelpCenter, Redirect, UiModificationsEntryPoint);

    const customiseSidebarTriggerRef = useCustomiseSidebarTriggerRef();

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { [REFERRER_QUERY_PARAM]: referrer } = useQueryParams();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const openSidebar = useCallback(() => toggleSidebar(true), [toggleSidebar]);
    const closeSidebar = useCallback(() => toggleSidebar(false), [toggleSidebar]);
    const openChatSidebarCallback = useCallback(() => openChatSideBarAction?.(), [openChatSideBarAction]);
    const closeChatSidebarCallback = useCallback(() => closeChatSideBarAction?.(), [closeChatSideBarAction]);

    const onSubmit = useCallback(() => setIsSubmitting(true), [setIsSubmitting]);
    const onSubmitComplete = useCallback(() => setIsSubmitting(false), [setIsSubmitting]);

    const shouldRenderAsPortalAnnouncement = (): boolean => {
        // for simplified projects, render side bar as announcement for agents with permission
        if (isProjectSimplified && !isProjectAdmin && canEditAnnouncement) {
            return true;
        }
        // in classic projects render sidebar as announcement for users who can't modify helpcenter, but can edit announcements
        if (!isProjectSimplified && !canCustomiseHelpCenter && canEditAnnouncement) {
            return true;
        }
        return false;
    };

    const renderVsaChatSidebar = () => {
        if (!portalId || !isChatSideBarOpen) return null;
        return (
            <LazySuspense fallback={null}>
                <LazyVirtualServiceAgentChatSidebar
                    isOpen
                    onOpen={openChatSidebarCallback}
                    onClose={closeChatSidebarCallback}
                    portalId={portalId}
                />
            </LazySuspense>
        );
    };

    const renderSidebar = () => {
        if (!canEditAnnouncement) return null;

        if (isKoshEnabledForDefaultHelpCenter() && !isBrandingLoaded) {
            // since check in shouldRenderAsPortalAnnouncement is dependent on "canCustomiseHelpCenter" which is coming from helpCenter model, we need to make sure branding relay call is completed
            return null;
        }

        // Render the new 'next-gen' side bar, either as just the Announcement for agents with permission or with all customisation features available for admins
        if (shouldRenderAsPortalAnnouncement()) {
            return (
                <LazySuspense fallback={null}>
                    <LazyPortalSettingsAnnouncementSidebar
                        isOpen={sideBarOpen}
                        onOpen={openSidebar}
                        onClose={closeSidebar}
                        triggerRef={customiseSidebarTriggerRef}
                    />
                </LazySuspense>
            );
        }

        return (
            <LazySuspense fallback={null}>
                <LazyPortalSettingsSidebar
                    isProjectSimplified={isProjectSimplified}
                    isOpen={sideBarOpen}
                    onOpen={openSidebar}
                    onClose={closeSidebar}
                    triggerRef={customiseSidebarTriggerRef}
                />
            </LazySuspense>
        );
    };

    const key = String(requestTypeId);

    const title =
        portalName &&
        getPortalHomeTitle({
            intl,
            portalName,
            requestTypeName,
            requestGroupId,
        });

    const shouldRenderDropdown = !!requestGroupId || !categories || !categories.length;
    const shouldShowRequestsInBreadcrumbs = !!shouldRenderDropdown && categories && !!categories.length;
    const isExternalLinksVisible = projectId && !validateRequestGroupIdPresent(requestGroupId) && !requestTypeId;
    const shouldShowSkeleton = projectId && requestTypeId; // show only on request create page (for now)
    const LoadingIndicator = shouldShowSkeleton ? SkeletonLoadingContainer : LoadingContainer;
    const cloudId = getCloudId();
    const { isVirtualAgentEnabled, isLoading: isVirtualAgentLoading } = useVirtualAgentAvailability({
        isLoggedIn,
        cloudId,
        projectId,
    });
    const shouldShowLearnByDoingExperimentBanner = shouldShowLearnByDoingBanner(isProjectAdmin, referrer);

    if (isCSMHelpCenter(getEnv().helpCenterType)) {
        return <Redirect to={'/'} />;
    }

    return (
        <DocumentTitle title={title} ignoreBaseTitle>
            <LoadingIndicator isLoading={loading} enableUFOHold ufoHoldName="portal-home-dumb-fallback">
                {/* Show UFOHold when announcement data is specifically loading */}
                {loading &&
                    !announcement &&
                    !isUseHelpCenterRefresh() &&
                    FeatureGates.checkGate('add_ufo_holds_to_request_create_form') && (
                        <UFOLoadHold name="announcement-banner-loading" />
                    )}
                {!!announcement && !isUseHelpCenterRefresh() && (
                    <AnnouncementBanner
                        actionSubjectId="portalAnnouncementBanner"
                        header={announcement.userLanguageHeader}
                        messageHtml={announcement.userLanguageMessageWiki}
                        InnerContainerComponent={AnnouncementInnerContainer}
                    />
                )}
                {requestGroupId && portalId && requestTypeId && !requestTypeBelongsToGroup && (
                    <Redirect to={`/portal/${portalId}/group/${requestGroupId}`} push={false} />
                )}
                <AnalyticsContext
                    data={{
                        attributes: {
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                            [REFERRER_QUERY_PARAM]: referrer,
                        },
                    }}
                >
                    {shouldShowLearnByDoingExperimentBanner && (
                        <Box testId="learn-by-doing-container" xcss={LearnByDoingContainerStyles}>
                            <LearnByDoing screenName={screenName} />
                        </Box>
                    )}

                    <PaperContent>
                        <UiModificationsEntryPoint portalId={portalId} requestTypeId={requestTypeId} />
                        <HeaderSinglePortalMode showRequestsInBreadcrumbs={shouldShowRequestsInBreadcrumbs} />
                        {projectId && (
                            <BlockedAppsBanner
                                page={getForgePageType(requestTypeId)}
                                isAtlassianManagedAccount={isAtlassianManagedAccount}
                                projectId={projectId}
                            />
                        )}
                        <HeaderConnectFragments
                            page={getConnectPageType(requestTypeId)}
                            portalId={portalId}
                            requestTypeId={requestTypeId}
                        />
                        <ForgeHeaderModule
                            page={getForgePageType(requestTypeId)}
                            projectId={projectId}
                            requestTypeId={requestTypeId}
                            portalId={portalId}
                        />
                        {/* Show UFOHold when virtual agent availability is still loading */}
                        {isVirtualAgentLoading && FeatureGates.checkGate('add_ufo_holds_to_request_create_form') && (
                            <UFOLoadHold name="virtual-agent-banner-loading" />
                        )}
                        {isVirtualAgentEnabled && (
                            <Box xcss={virtualBannerStyles}>
                                <VirtualAgentBanner cloudId={cloudId} portalId={portalId} />
                            </Box>
                        )}
                        {!requestGroupId && <StyledCategoryList portalId={portalId} />}
                        {shouldRenderDropdown && (
                            <StyledFormPicker
                                autoFocus
                                portalId={portalId}
                                requestGroupId={requestGroupId}
                                requestTypeId={requestTypeId}
                                isDisabled={isSubmitting}
                            />
                        )}
                        <RequestCreateFormHelpText portalId={portalId} requestTypeId={requestTypeId} />
                        {/* Using key to force unmount form in order to avoid field values are still present when navigate to different request type.
                The reason is react-select, date picker manages its own state and our components store async call result in state such as form field error. Hence, resetting the form wont clear their state */}
                        {FeatureGates.checkGate('chunk_for_request_create_form_with_proform') && hasProformaForm ? (
                            <RequestCreateFormWithProforma
                                portalId={portalId}
                                requestTypeId={requestTypeId}
                                key={key}
                                onSubmit={onSubmit}
                                onSubmitComplete={onSubmitComplete}
                            />
                        ) : (
                            <RequestCreateForm
                                portalId={portalId}
                                requestTypeId={requestTypeId}
                                key={key}
                                onSubmit={onSubmit}
                                onSubmitComplete={onSubmitComplete}
                            />
                        )}
                        {isExternalLinksVisible && (
                            <ExternalLinksListWrapper>
                                <ExternalLinks projectId={projectId} />
                            </ExternalLinksListWrapper>
                        )}
                        {FeatureGates.checkGate('rovo_service_agent_widget_experience') &&
                            projectId &&
                            isAtlassianManagedAccount && (
                                <RovoServiceAgentWidgetInjector
                                    siteId={cloudId}
                                    widgetId={JiraProjectAri.create({
                                        projectId: projectId.toString(),
                                        siteId: cloudId,
                                    }).toString()}
                                    surface="PORTAL"
                                />
                            )}
                        <FooterConnectFragments
                            page={getConnectPageType(requestTypeId)}
                            portalId={portalId}
                            requestTypeId={requestTypeId}
                        />
                        <ForgeFooterModule
                            page={getForgePageType(requestTypeId)}
                            projectId={projectId}
                            requestTypeId={requestTypeId}
                            portalId={portalId}
                        />
                    </PaperContent>
                </AnalyticsContext>
                <ErrorBoundary
                    packageName={ScreenName.HELP_CENTER_CUSTOMIZE_ANNOUNCEMENT_SIDEBAR}
                    id={'portal-home-sidebar'}
                >
                    {sideBarOpen && renderSidebar()}
                </ErrorBoundary>
                <ErrorBoundary
                    packageName={ScreenName.HELP_CENTER_VSA_CHAT_SIDEBAR}
                    id={'portal-home-vsa-chat-sidebar'}
                >
                    {isChatSideBarOpen && renderVsaChatSidebar()}
                </ErrorBoundary>
                <SidebarToggle openSidebar={openSidebar} closeSidebar={closeSidebar} />
            </LoadingIndicator>
        </DocumentTitle>
    );
};

export const PortalHomeDumb = withErrorPage(PortalHome, { isEmptyTraceIdLogExcluded: true });

export default injectIntl(PortalHomeDumb);

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const AnnouncementInnerContainer = styled(PaperContent)`
    /* Reset top padding from the paper content. */
    padding-top: 0;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ExternalLinksListWrapper = styled.div`
    margin-top: ${token('space.400', '32px')};
`;

// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const StyledFormPicker = styled(FormPicker)`
    margin: ${token('space.400', '32px')} 0 ${token('space.100', '8px')};
    position: relative;
`;

// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const StyledCategoryList = styled(CategoryList)`
    margin-top: ${token('space.400', '32px')};
`;

const virtualBannerStyles = xcss({
    marginTop: 'space.300',
    marginBottom: 'space.200',
});

const LearnByDoingContainerStyles = xcss({
    height: '266px',
    display: 'none',
    [media.above.md]: {
        display: 'flex',
    },
});
